import { Seo } from '@/components/Seo';
import { JsonLd, makeFAQSchema, makeBreadcrumbSchema } from '@/components/Schema';
import { FAQ } from '@/components/FAQ';
import { FinalCTA } from '@/components/FinalCTA';
import { Link } from 'react-router-dom';
import { Sparkles, GraduationCap, Wrench, HeartHandshake, Award, Target, Lightbulb } from 'lucide-react';

const faqs = [
  {
    question: 'Can I make a resume with no work experience?',
    answer: 'Yes. Focus on education, skills, projects, volunteer work, certifications, and a clear objective.',
  },
  {
    question: 'What should a first resume include?',
    answer: 'Your contact information, a short objective or summary, education, skills, projects, volunteer work, and any certifications or awards.',
  },
  {
    question: 'How can AI help if I have no experience?',
    answer: 'The AI rewrite can turn simple activities like helped at events or built a class project into achievement-focused bullets.',
  },
  {
    question: 'Is this resume builder free for first-time job seekers?',
    answer: 'Yes. You can build and download your resume for free. You get 2 free AI rewrites per browser ID.',
  },
  {
    question: 'Will my resume look empty?',
    answer: 'Not if you use the Student template and fill in all relevant sections. The template is designed to present limited experience professionally.',
  },
];

export function ResumeBuilderForNoExperience() {
  return (
    <>
      <Seo
        title="Resume Builder for No Experience | Free PDF Download"
        description="Create your first resume with no work experience. Free, ATS-friendly, and no sign-up required. AI bullet rewriter included."
        path="/resume-builder-for-no-experience"
      />
      <JsonLd schema={makeFAQSchema(faqs)} />
      <JsonLd schema={makeBreadcrumbSchema([{ name: 'Resume Builder for No Experience', path: '/resume-builder-for-no-experience' }])} />

      <section className="hero-glow py-24 md:py-32">
        <div className="mx-auto max-w-[800px] px-6 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#00D4AA]/15">
            <Lightbulb size={32} className="text-[#00D4AA]" />
          </div>
          <h1 className="text-balance mt-6 text-4xl font-extrabold text-white md:text-5xl">
            Resume Builder for No Experience — Create Your First Resume
          </h1>
          <p className="mx-auto mt-6 max-w-[680px] text-lg text-white/75">
            Yes. You can build a strong first resume even without work experience. Focus on what you have learned, built, and volunteered for.
          </p>
          <Link
            to="/app/create"
            className="btn-primary mt-8 inline-flex items-center justify-center rounded-[10px] px-8 py-4 text-base font-semibold"
          >
            Create My First Resume
          </Link>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-[1200px] px-6">
          <div className="text-center">
            <span className="rounded-full bg-[#E7F1FA] px-4 py-1.5 text-sm font-semibold text-[#0A2540]">
              What to include
            </span>
            <h2 className="mt-4 text-3xl font-bold text-[#0A2540] md:text-4xl">
              What to put on a resume with no experience
            </h2>
          </div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { icon: GraduationCap, title: 'Education', desc: 'School, expected graduation, major, and relevant coursework.' },
              { icon: Wrench, title: 'Skills', desc: 'Technical, language, and transferable skills that match the job.' },
              { icon: HeartHandshake, title: 'Volunteer Work', desc: 'Community service, clubs, or event organizing count.' },
              { icon: Award, title: 'Certifications', desc: 'Online courses, licenses, or certificates that prove knowledge.' },
              { icon: Target, title: 'Objective', desc: 'A short statement explaining what role you are targeting and why.' },
              { icon: Sparkles, title: 'Projects', desc: 'Class, personal, or group projects with clear outcomes.' },
            ].map((s) => (
              <div key={s.title} className="rounded-2xl border border-[#E2E8F0] bg-[#F8FAFC] p-6 transition duration-300 hover:bg-white hover:shadow-sm">
                <div className="flex h-12 w-12 items-center justify-center rounded-[10px] bg-[#00D4AA]/15">
                  <s.icon size={24} className="text-[#00D4AA]" />
                </div>
                <h3 className="mt-4 text-lg font-semibold text-[#0A2540]">{s.title}</h3>
                <p className="mt-2 text-[#4A5568]">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#0A2540] py-16 md:py-24">
        <div className="mx-auto max-w-[1200px] px-6">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
            <div>
              <span className="rounded-full bg-[#00D4AA]/15 px-4 py-1.5 text-sm font-semibold text-[#00D4AA]">
                AI help
              </span>
              <h2 className="mt-4 text-3xl font-bold text-white md:text-4xl">
                How AI turns limited experience into strong bullets
              </h2>
              <p className="mt-4 text-white/75">
                Even simple activities can sound professional when framed with action verbs and outcomes. Our AI rewrite helps you describe what you did, what you learned, and the impact you made.
              </p>
              <ul className="mt-6 space-y-3 text-white/75">
                <li className="flex items-start gap-2">
                  <span className="text-[#00D4AA]">✓</span>
                  Helped at a school event → Organized registration for 120+ attendees.
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#00D4AA]">✓</span>
                  Built a class project → Developed a working prototype in 4 weeks.
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#00D4AA]">✓</span>
                  Volunteered weekly → Committed 60+ hours to community outreach.
                </li>
              </ul>
            </div>
            <div className="rounded-2xl border border-white/[0.12] bg-[#112940] p-8">
              <div className="space-y-4">
                <div className="rounded-lg bg-[#0A2540] p-4 text-white/75">
                  Before: I helped with social media.
                </div>
                <div className="rounded-lg border border-[#00D4AA]/30 bg-[#00D4AA]/10 p-4 text-white">
                  After: Managed social media content schedule, increasing engagement by 25% over 2 months.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#F6F9FC] py-16">
        <div className="mx-auto max-w-[800px] px-6">
          <div className="text-center">
            <span className="rounded-full bg-[#E7F1FA] px-4 py-1.5 text-sm font-semibold text-[#0A2540]">
              FAQ
            </span>
            <h2 className="mt-4 text-3xl font-bold text-[#0A2540]">
              No Experience Resume FAQ
            </h2>
          </div>
          <div className="mt-10">
            <FAQ items={faqs} />
          </div>
        </div>
      </section>

      <FinalCTA
        title="Create your first resume today"
        subtitle="Free, no sign-up, and ATS-friendly."
        buttonText="Create My First Resume"
        buttonTo="/app/create"
      />
    </>
  );
}

export default ResumeBuilderForNoExperience;
